
import React from 'react';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings, LogIn, Sun, Moon, Menu, X, LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NavLink } from "./NavLink";
import LanguageSelector from "./LanguageSelector";
import AccessibilityMenu from "./AccessibilityMenu";
import NotificationBell from "../NotificationBell";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/hooks/use-theme";

interface NavActionsProps {
  navItems: Array<{
    label: string;
    href: string;
    icon: LucideIcon;
    badge?: number | null;
  }>;
  isMobile: boolean;
  isOpen: boolean;
  toggleNav: () => void;
}

const NavActions = ({ 
  navItems, 
  isMobile,
  isOpen,
  toggleNav
}: NavActionsProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { actualTheme, setTheme } = useTheme();
  const isDarkMode = actualTheme === 'dark';

  const toggleTheme = () => {
    setTheme(isDarkMode ? 'light' : 'dark');
  };

  return (
    <div className="flex flex-col items-end gap-4 relative">
      <div className="flex items-center space-x-2">
        <LanguageSelector />
        
        <AccessibilityMenu />

        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleTheme}
          className="rounded-full w-9 h-9 p-0"
          title={isDarkMode ? t('theme.light') : t('theme.dark')}
        >
          {isDarkMode ? (
            <Sun className="h-5 w-5 flex-shrink-0" />
          ) : (
            <Moon className="h-5 w-5 flex-shrink-0" />
          )}
          <span className="sr-only">{isDarkMode ? t('theme.light') : t('theme.dark')}</span>
        </Button>
        
        <NotificationBell />
        
        {!isMobile && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center hover:bg-secondary transition-colors">
                <Settings className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>Menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 mt-2 bg-popover border border-border z-50">
              {navItems.slice(4).map((item) => (
                <DropdownMenuItem key={item.href} asChild className="hover:bg-secondary cursor-pointer">
                  <NavLink to={item.href} className="flex items-center py-2 w-full" icon={item.icon}>
                    {item.label}
                  </NavLink>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem className="hover:bg-secondary cursor-pointer">
                <NavLink to="/auth" className="flex items-center py-2 w-full" icon={LogIn}>
                  {t('login')}
                </NavLink>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleNav}
            className="p-1"
            aria-label={isOpen ? t('nav.closeMenu') || 'Fermer le menu' : t('nav.openMenu') || 'Ouvrir le menu'}
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="h-6 w-6 flex-shrink-0" /> : <Menu className="h-6 w-6 flex-shrink-0" />}
            <span className="sr-only">{isOpen ? t('nav.closeMenu') || 'Fermer le menu' : t('nav.openMenu') || 'Ouvrir le menu'}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default NavActions;
